import React, { useState, JSX } from 'react';
import { Button, Col, Form, FormFeedback, FormGroup, Input, Label, Row } from 'reactstrap';
import * as yup from 'yup';
import { useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ApiAgency, ApiAddress, ApiBaseData } from '@ibe/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import fallback from '../../../_Translations/generated/agency-profile-address.en.t.json';
import Keys from '../../../_Translations/generated/agency-profile-address.en.t.json.keys';
import useTranslation from '../../Hooks/useTranslation';
import { Select } from '../Form/Select/Select';

interface Props {
  agency: ApiAgency;
  countries: ApiBaseData[];
  onSubmit: (agency: ApiAddress) => void;
}

type AgencyProfileAddressFormValues = {
  street: string;
  postalCode: string;
  city: string;
  countryCode: string;
};

const AgencyProfileAddressForm = (props: Props): JSX.Element => {
  const { agency, countries, onSubmit } = props;
  const [isEditing, setIsEditing] = useState(false);
  const [countryCode, setCountryCode] = useState<string>(agency.address.countryCode);
  const { t } = useTranslation('agency-profile-address', fallback, Keys);
  const schema: yup.ObjectSchema<AgencyProfileAddressFormValues> = yup.object({
    street: yup.string().required(t(Keys.requiredField)),
    postalCode: yup.string().required(t(Keys.requiredField)),
    city: yup.string().required(t(Keys.requiredField)),
    countryCode: yup.string().defined()
  });

  const defaultValues: AgencyProfileAddressFormValues = {
    street: agency.address.street,
    postalCode: agency.address.postalCode,
    city: agency.address.city,
    countryCode: agency.address.countryCode
  };

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors }
  } = useForm<AgencyProfileAddressFormValues, unknown, AgencyProfileAddressFormValues>({
    resolver: yupResolver(schema),
    defaultValues
  });

  const findCountry = (countryCodeVal: string): ApiBaseData => {
    return (
      countries.find((country: ApiBaseData) => country.code === countryCodeVal) || countries[0]
    );
  };

  const submit: SubmitHandler<AgencyProfileAddressFormValues> = (address): void => {
    onSubmit(address);
    setIsEditing(false);
  };

  /* eslint-disable react/jsx-props-no-spreading */
  return (
    <Form onSubmit={handleSubmit(submit)}>
      <Row>
        <Col md={6}>
          <FormGroup>
            <Label className="font-weight-light" htmlFor="street">
              {t(Keys.street)}
            </Label>
            <Input
              type="text"
              id="street"
              placeholder={t(Keys.street)}
              disabled={!isEditing}
              invalid={!!errors.street}
              {...register('street')}
              onChange={(e): void => {
                setValue('street', e.target.value);
              }}
              autoComplete="address-line1"
            />
            {errors.street && (
              <FormFeedback>
                <FontAwesomeIcon icon={faExclamationTriangle} />
                {errors.street.message}
              </FormFeedback>
            )}
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label className="font-weight-light" htmlFor="postalCode">
              {t(Keys.postalCode)}
            </Label>
            <Input
              type="text"
              id="postalCode"
              placeholder={t(Keys.postalCode)}
              disabled={!isEditing}
              invalid={!!errors.postalCode}
              {...register('postalCode')}
              onChange={(e): void => {
                setValue('postalCode', e.target.value);
              }}
              autoComplete="postal-code"
            />
            {errors.postalCode && (
              <FormFeedback>
                <FontAwesomeIcon icon={faExclamationTriangle} />
                {errors.postalCode.message}
              </FormFeedback>
            )}
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <FormGroup>
            <Label className="font-weight-light" htmlFor="city">
              {t(Keys.city)}
            </Label>
            <Input
              type="text"
              id="city"
              placeholder={t(Keys.city)}
              disabled={!isEditing}
              invalid={!!errors.city}
              {...register('city')}
              onChange={(e): void => {
                setValue('city', e.target.value);
              }}
              autoComplete="address-level2"
            />
            {errors.city && (
              <FormFeedback>
                <FontAwesomeIcon icon={faExclamationTriangle} />
                {errors.city.message}
              </FormFeedback>
            )}
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label className="font-weight-light" htmlFor="countryCode">
              {t(Keys.country)}
            </Label>
            <Select
              options={countries}
              selectedOption={findCountry(countryCode)}
              getLabel={(country): string => (country ? country.description : '')}
              getValue={(country): string => (country ? country.code : '')}
              isDisabled={!isEditing}
              onChange={(country): void => {
                setCountryCode(country.code);
                setValue('countryCode', country.code);
              }}
            />
            {errors.countryCode && (
              <FormFeedback>
                <FontAwesomeIcon icon={faExclamationTriangle} />
                {errors.countryCode.message}
              </FormFeedback>
            )}
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col>
          {!isEditing && (
            <Button color="primary" onClick={(): void => setIsEditing(true)}>
              {t(Keys.edit)}
            </Button>
          )}
          {isEditing && (
            <>
              <Button
                color="secondary"
                onClick={(): void => {
                  setIsEditing(false);
                  reset(defaultValues);
                  setCountryCode(defaultValues.countryCode);
                }}
              >
                {t(Keys.cancel)}
              </Button>
              <Button className="ml-3" color="primary" type="submit">
                {t(Keys.save)}
              </Button>
            </>
          )}
        </Col>
      </Row>
    </Form>
  );
};

export default AgencyProfileAddressForm;
